/* =================================================================
   Operating Intelligence — rich per-agent profile data.

   The home Operating Intelligence section + Footer + AIConcierge all
   share the *avatars* through `teamAvatars.js`. This module adds the
   long-form profile content that powers the dedicated /operating-
   intelligence page only — capabilities, integrations, sample uses,
   collaboration handoffs — kept in one place so EN and ES stay in sync.

   Each entry is keyed by the same `key` used by TEAM_AVATARS so the
   page can do a simple zip-by-index render.
   ================================================================= */
export const OI_PROFILES = {
  en: [
    {
      key: 'rachel',
      role: 'Client Concierge',
      tagline: 'Every guest conversation. Answered.',
      summary:
        'Rachel is the always-on receptionist of your restaurant. She handles reservations, takeout, catering inquiries and guest questions across WhatsApp, voice and web — in your brand voice, in English or Spanish.',
      capabilities: [
        'Live reservation and order capture 24/7',
        'Bilingual responses in English and Spanish',
        'Multi-channel: WhatsApp, voice, web chat',
        'Smart routing to humans when needed',
        'Real-time guest preference memory',
      ],
      integrations: ['Twilio', 'WhatsApp Business', 'OpenTable', 'Toast'],
      sampleUse:
        '“Hi, can I book a table for six on Friday at 8pm?” Rachel checks availability, confirms instantly, and pushes the reservation into OpenTable — without anyone in the restaurant lifting a phone.',
    },
    {
      key: 'stan',
      role: 'Lead Generation',
      tagline: 'New catering accounts. Discovered. Qualified.',
      summary:
        'Stan continuously prospects companies, corporate offices, event organizers and event venues in your delivery radius, scores them by catering fit, and prepares the first conversation. He turns local market research into a clean, ranked catering pipeline your sales team can actually close.',
      capabilities: [
        'Continuous prospecting of local companies and offices',
        'Catering-fit scoring (headcount, order patterns, budget)',
        'First-touch outreach drafts in your house voice',
        'Pipeline enrichment (decision-makers, emails, phones)',
        'Hand-off briefs for the human sales team',
      ],
      integrations: ['Apollo', 'HubSpot', 'Clearbit', 'LinkedIn Sales Nav'],
      sampleUse:
        'Stan identifies 18 new offices with 40+ employees that opened this quarter within 5 km of the restaurant, scores them by likely catering spend, and queues the top 4 for Penny to research before Rachel reaches out with a tailored catering menu.',
    },
    {
      key: 'eva',
      role: 'Operations',
      tagline: 'The work behind the work. Coordinated.',
      summary:
        'Eva is the operational backbone. She coordinates handoffs between agents, schedules follow-ups, syncs internal documents, and makes sure nothing falls between the cracks. She is the conductor of the Operating Team.',
      capabilities: [
        'Inter-agent task coordination',
        'Calendar and meeting orchestration',
        'Document syncing and versioning',
        'Internal SLA monitoring',
        'Process automation across tools',
      ],
      integrations: ['Notion', 'Google Workspace', 'Zapier', 'Slack'],
      sampleUse:
        'Eva detects that a discovery call ended without a follow-up, schedules a reminder for the human owner, drafts the summary in Notion, and asks Sonny to prepare a tailored case study.',
    },
    {
      key: 'sonny',
      role: 'Social Media',
      tagline: 'Always-on brand presence. Without burnout.',
      summary:
        'Sonny runs the Kitxens brand across LinkedIn, Instagram and X. He schedules campaigns, repurposes long-form content, monitors mentions and surfaces conversations worth a human reply.',
      capabilities: [
        'Multi-platform content scheduling',
        'Long-form to short-form repurposing',
        'Brand mention monitoring',
        'Engagement triage',
        'Campaign performance reports',
      ],
      integrations: ['Buffer', 'Meta Business', 'LinkedIn', 'X / Twitter'],
      sampleUse:
        'Sonny turns Penny’s new SEO article into a five-post LinkedIn series, schedules an Instagram carousel, and flags two mentions where a partner is asking about pricing.',
    },
    {
      key: 'penny',
      role: 'SEO Content',
      tagline: 'Be found before competitors. Everywhere.',
      summary:
        'Penny researches what restaurants are searching for — and what AI engines are answering — and produces visibility content that ranks on Google, Maps and generative engines. She is the writer behind Index AI.',
      capabilities: [
        'Keyword and topic discovery',
        'AI-search optimisation (GEO)',
        'Long-form article production',
        'Local-SEO landing pages',
        'Content performance feedback loop',
      ],
      integrations: ['Ahrefs', 'Google Search Console', 'Google Business', 'OpenAI'],
      sampleUse:
        'Penny notices "best catering for tech offices in Austin" is rising and ungoverned by any single brand. She writes a definitive guide, optimises it for ChatGPT answers, and ships it for review.',
    },
    {
      key: 'linda',
      role: 'Legal',
      tagline: 'Operational paperwork. Quietly handled.',
      summary:
        'Linda prepares and reviews operational documents: client agreements, supplier contracts, NDA templates, terms updates. She is not a substitute for legal counsel — she removes friction from the day-to-day so the humans can focus on the work that matters.',
      capabilities: [
        'Contract and agreement drafting',
        'Document review and red-lining',
        'Template library maintenance',
        'Compliance checklist tracking',
        'Hand-off briefs for outside counsel',
      ],
      integrations: ['DocuSign', 'Notion', 'Google Drive'],
      sampleUse:
        'Linda spots that a new partner agreement still references an outdated clause, red-lines it, suggests a corrected version, and queues it for human review before sending.',
    },
  ],
  es: [
    {
      key: 'rachel',
      role: 'Concierge de Clientes',
      tagline: 'Cada conversación con clientes. Atendida.',
      summary:
        'Rachel es la recepcionista siempre activa de tu restaurante. Atiende reservas, pedidos, consultas de catering y dudas de huéspedes en WhatsApp, voz y web — con la voz de tu marca, en inglés o español.',
      capabilities: [
        'Captura de reservas y pedidos 24/7',
        'Respuestas bilingües (EN/ES)',
        'Multicanal: WhatsApp, voz, web chat',
        'Routing inteligente a humanos cuando hace falta',
        'Memoria de preferencias de cada cliente',
      ],
      integrations: ['Twilio', 'WhatsApp Business', 'OpenTable', 'Toast'],
      sampleUse:
        '"Hola, ¿puedo reservar para seis el viernes a las 8?" Rachel verifica disponibilidad, confirma al instante, y envía la reserva a OpenTable — sin que nadie en el restaurante tenga que tocar un teléfono.',
    },
    {
      key: 'stan',
      role: 'Generación de Leads',
      tagline: 'Nuevas cuentas de catering. Descubiertas. Calificadas.',
      summary:
        'Stan prospecta continuamente empresas, oficinas corporativas, organizadores de eventos y salones en tu radio de reparto, los puntúa por encaje de catering, y prepara la primera conversación. Convierte el research local en un pipeline de catering que tu equipo comercial puede cerrar.',
      capabilities: [
        'Prospección continua de empresas y oficinas locales',
        'Scoring de encaje de catering (plantilla, patrones, presupuesto)',
        'Borradores de outreach en la voz de tu marca',
        'Enriquecimiento de pipeline (decisores, emails, teléfonos)',
        'Briefings de handoff para el equipo comercial humano',
      ],
      integrations: ['Apollo', 'HubSpot', 'Clearbit', 'LinkedIn Sales Nav'],
      sampleUse:
        'Stan identifica 18 oficinas nuevas de +40 empleados abiertas este trimestre en un radio de 5 km, las puntúa por gasto probable en catering, y prepara las 4 mejores para que Penny investigue antes de que Rachel envíe una propuesta de catering personalizada.',
    },
    {
      key: 'eva',
      role: 'Operaciones',
      tagline: 'El trabajo detrás del trabajo. Coordinado.',
      summary:
        'Eva es la columna vertebral operativa. Coordina handoffs entre agentes, agenda follow-ups, sincroniza documentos internos, y asegura que nada se cae entre las grietas. Es la directora de orquesta del Operating Team.',
      capabilities: [
        'Coordinación de tareas entre agentes',
        'Orquestación de calendarios y reuniones',
        'Sincronización y versionado de documentos',
        'Monitoreo de SLAs internos',
        'Automatización de procesos cross-tool',
      ],
      integrations: ['Notion', 'Google Workspace', 'Zapier', 'Slack'],
      sampleUse:
        'Eva detecta que una llamada de descubrimiento terminó sin follow-up, agenda un recordatorio para el responsable humano, redacta el resumen en Notion, y le pide a Sonny preparar un caso de estudio.',
    },
    {
      key: 'sonny',
      role: 'Redes Sociales',
      tagline: 'Presencia de marca siempre activa. Sin burnout.',
      summary:
        'Sonny gestiona la marca Kitxens en LinkedIn, Instagram y X. Programa campañas, reutiliza contenido largo, monitorea menciones y eleva las conversaciones que valen una respuesta humana.',
      capabilities: [
        'Calendario de contenido multi-plataforma',
        'Repurposing de long-form a short-form',
        'Monitoreo de menciones de marca',
        'Triage de engagement',
        'Reportes de performance de campaña',
      ],
      integrations: ['Buffer', 'Meta Business', 'LinkedIn', 'X / Twitter'],
      sampleUse:
        'Sonny convierte un artículo SEO de Penny en una serie de cinco posts de LinkedIn, programa un carrusel de Instagram, y marca dos menciones donde un partner pregunta por precios.',
    },
    {
      key: 'penny',
      role: 'Contenido SEO',
      tagline: 'Aparece antes que tu competencia. En todos lados.',
      summary:
        'Penny investiga qué buscan los restaurantes — y qué responden los motores de IA — y produce contenido de visibilidad que rankea en Google, Maps y motores generativos. Es la voz detrás de Index AI.',
      capabilities: [
        'Descubrimiento de keywords y temas',
        'Optimización para AI search (GEO)',
        'Producción de artículos long-form',
        'Landings de SEO local',
        'Feedback loop de performance de contenido',
      ],
      integrations: ['Ahrefs', 'Google Search Console', 'Google Business', 'OpenAI'],
      sampleUse:
        'Penny detecta que "mejor catering para oficinas tech en Austin" está creciendo sin que ninguna marca lo lidere. Escribe la guía definitiva, la optimiza para respuestas de ChatGPT, y la deja lista para revisión.',
    },
    {
      key: 'linda',
      role: 'Legal',
      tagline: 'Papeleo operativo. Silenciosamente resuelto.',
      summary:
        'Linda prepara y revisa documentos operativos: contratos con clientes, acuerdos con proveedores, plantillas de NDA, actualizaciones de términos. No reemplaza al abogado — quita la fricción del día a día para que los humanos se enfoquen en el trabajo que importa.',
      capabilities: [
        'Redacción de contratos y acuerdos',
        'Revisión y red-lining de documentos',
        'Mantenimiento de la biblioteca de plantillas',
        'Tracking de checklists de compliance',
        'Briefings de handoff a abogados externos',
      ],
      integrations: ['DocuSign', 'Notion', 'Google Drive'],
      sampleUse:
        'Linda detecta que un nuevo acuerdo con un partner todavía referencia una cláusula desactualizada, la marca, sugiere una versión corregida, y la deja en cola para revisión humana antes de enviar.',
    },
  ],
};

/* "How they work together" — a single canonical flow that maps the
   typical day inside the Kitxens Operating Team. Each step references
   the agent by key so the renderer can pull the avatar + theme. */
export const OI_FLOW = {
  en: [
    { from: 'stan',   to: 'penny',  label: 'Discover',  body: 'Stan surfaces new companies worth pitching for catering.' },
    { from: 'penny',  to: 'sonny',  label: 'Research',  body: 'Penny writes the angle that matters to them.' },
    { from: 'sonny',  to: 'rachel', label: 'Amplify',   body: 'Sonny puts that angle in front of decision-makers.' },
    { from: 'rachel', to: 'eva',    label: 'Engage',    body: 'Rachel handles the first conversation, in real time.' },
    { from: 'eva',    to: 'linda',  label: 'Coordinate', body: 'Eva schedules the work and aligns the team.' },
    { from: 'linda',  to: 'stan',   label: 'Formalize',  body: 'Linda prepares the paperwork to close the loop.' },
  ],
  es: [
    { from: 'stan',   to: 'penny',  label: 'Descubrir',   body: 'Stan identifica empresas que vale la pena contactar para catering.' },
    { from: 'penny',  to: 'sonny',  label: 'Investigar',  body: 'Penny escribe el ángulo que les importa.' },
    { from: 'sonny',  to: 'rachel', label: 'Amplificar',  body: 'Sonny pone ese ángulo frente a los decisores.' },
    { from: 'rachel', to: 'eva',    label: 'Conversar',   body: 'Rachel maneja la primera conversación, en tiempo real.' },
    { from: 'eva',    to: 'linda',  label: 'Coordinar',   body: 'Eva agenda el trabajo y alinea al equipo.' },
    { from: 'linda',  to: 'stan',   label: 'Formalizar',  body: 'Linda prepara el papeleo para cerrar el ciclo.' },
  ],
};
